import type { WalkNet } from './walkNet';

/** The shortest way to one place from every point of the net: each point's next point on it, and
 *  how far the place is; -1 and infinity where the place cannot be reached. */
export interface RouteTree {
  readonly next: Int32Array;
  readonly distance: Float64Array;
}

/* A binary heap of points by distance. A point is pushed again when a shorter way is found, and
   the stale entries are skipped as they come out. */
function createQueue() {
  const nodes: number[] = [],
    costs: number[] = [];
  return {
    get size(): number {
      return nodes.length;
    },
    push(node: number, cost: number): void {
      let i = nodes.length;
      nodes.push(node);
      costs.push(cost);
      while (i > 0) {
        const up = (i - 1) >> 1;
        if (costs[up]! <= cost) break;
        nodes[i] = nodes[up]!;
        costs[i] = costs[up]!;
        i = up;
      }
      nodes[i] = node;
      costs[i] = cost;
    },
    pop(): [number, number] {
      const top: [number, number] = [nodes[0]!, costs[0]!];
      const node = nodes.pop()!,
        cost = costs.pop()!;
      const size = nodes.length;
      if (size > 0) {
        let i = 0;
        for (;;) {
          let child = 2 * i + 1;
          if (child >= size) break;
          if (child + 1 < size && costs[child + 1]! < costs[child]!) child++;
          if (costs[child]! >= cost) break;
          nodes[i] = nodes[child]!;
          costs[i] = costs[child]!;
          i = child;
        }
        nodes[i] = node;
        costs[i] = cost;
      }
      return top;
    },
  };
}

/** Dijkstra from the place outward over the net. */
export function routeTree(net: WalkNet, place: number): RouteTree {
  const next = new Int32Array(net.count).fill(-1);
  const distance = new Float64Array(net.count).fill(Infinity);
  const queue = createQueue();
  distance[place] = 0;
  queue.push(place, 0);
  while (queue.size > 0) {
    const [at, cost] = queue.pop();
    if (cost > distance[at]!) continue;
    for (let e = net.start[at]!; e < net.start[at + 1]!; e++) {
      const to = net.to[e]!,
        reached = cost + net.length[e]!;
      if (reached >= distance[to]!) continue;
      distance[to] = reached;
      next[to] = at;
      queue.push(to, reached);
    }
  }
  return { next, distance };
}
